import { FC, useEffect } from 'react';
import { Drawer as MuiDrawer } from '@mui/material';
import { useEscapeKey } from '@csp/csp-web-hooks';
import { LoadingOverlay } from '../../feedback/progress/LoadingOverlay';
import { AlertError } from '../../lab/alert/AlertError';
import { SxProps } from '../../../common/system/SxProps';
import { Box } from '../../layout/box/Box';
import { Loading } from '../../feedback/progress/Loading';
import { DrawerHeader } from './DrawerHeader';
import { TemporaryDrawerProps } from './model/TemporaryDrawerProps';
import { drawerScrollTop } from './drawerScrollTop';
import { TemporaryDrawerMapper } from './mapper/TemporaryDrawerMapper';

export const TemporaryDrawer: FC<TemporaryDrawerProps> = ({
  header,
  onClose,
  actions,
  anchor = 'right',
  id,
  isLoading,
  size = 'medium',
  transition = false,
  ariaLabelClose,
  isLoadingInitial,
  children,
  error,
  errorAction,
  preventDrawerClose = !!actions, // Prevent Esc closing when actions are defined to avoid unwanted close - for example clicking Esc when select box is open
  ...props
}) => {
  const widthSize = TemporaryDrawerMapper.calcSizeLgUpWidth(size);
  const sxDrawer: SxProps = {
    '.MuiDrawer-paperAnchorRight': {
      overflowX: 'hidden',
      width: { xs: '100%', lg: widthSize },
      // TODO: [Saman M]: Fixed issue regarding side-out transition not work anymore
      transition: transition ? 'width 0.5s ease-in-out !important' : 'none',
    },
  };

  const sxBox: SxProps = {
    minHeight: { xs: 'calc(100vh - 5.7rem)', sm: 'unset' },
    height: { xs: 'auto', sm: 'unset' },
    justifyContent: 'center',
    position: { xs: 'relative', sm: 'absolute' },
    boxSizing: 'border-box',
    width: '100%',
    px: { xs: 3, sm: 14 },
    py: 1,
    ':after': {
      content: '""',
      display: 'block',
      height: '10rem',
    },
  };

  useEffect(() => {
    if (error && id) {
      drawerScrollTop(id);
    }
  }, [error, id]);

  const { ref } = useEscapeKey<HTMLDivElement>(preventDrawerClose ? undefined : onClose);

  return (
    <MuiDrawer id={id} anchor={anchor} ref={ref} sx={{ ...sxDrawer }} {...props}>
      <DrawerHeader header={header} onClose={onClose} ariaLabelClose={ariaLabelClose} />
      {isLoading && <LoadingOverlay width={widthSize} isLoading={isLoading} />}

      <Box mt={2}>
        <Box sx={sxBox}>
          {error && <AlertError fullWidth message={error} action={errorAction} mb={2.5} />}
          <Loading isLoading={isLoadingInitial}>{children}</Loading>
          {actions}
        </Box>
      </Box>
    </MuiDrawer>
  );
};
